/**
 * Results tab — vital signs, laboratory panels, and imaging findings.
 */
import { FC } from 'react'
import { FlaskConical } from 'lucide-react'
import type { PatientPanelData } from '../types'
import { VitalTable } from '../shared/VitalTable'
import { LabGroupTable } from '../shared/LabGroupTable'
import { ImagingFindings } from '../shared/ImagingFindings'
import { PanelSkeleton } from '../shared/PanelSkeleton'

export const ResultsTab: FC<{ data: PatientPanelData }> = ({ data }) => {
  const hasVitals = data.vitals && data.vitals.length > 0
  const hasLabs = data.labs && data.labs.length > 0
  const hasImaging = data.imaging && data.imaging.length > 0
  const hasAny = hasVitals || hasLabs || hasImaging

  // Loading state
  if (data.isGenerating && !hasAny) {
    return (
      <div className="flex-1 overflow-y-auto custom-scrollbar p-3">
        <PanelSkeleton lines={8} />
      </div>
    )
  }

  // Empty state
  if (!hasAny) {
    return (
      <div className="flex-1 overflow-y-auto custom-scrollbar p-3 flex items-center justify-center">
        <p className="text-xs text-muted-foreground opacity-40">
          No results data available
        </p>
      </div>
    )
  }

  // Total abnormal lab count across all groups
  const totalAbnl = data.labs
    ? data.labs.reduce((sum, group) => sum + group.labs.filter(l => l.flag).length, 0)
    : 0

  return (
    <div className="flex-1 overflow-y-auto custom-scrollbar p-3 space-y-3">
      {/* Vital Signs */}
      {hasVitals && <VitalTable vitals={data.vitals!} />}

      {/* Laboratory */}
      {hasLabs && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <FlaskConical className="w-3 h-3 text-muted-foreground" />
            <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">
              Laboratory
            </span>
            {totalAbnl > 0 && (
              <span className="text-[8px] font-bold text-clinical-coral bg-red-500/10 px-1.5 py-0.5 rounded-full">
                {totalAbnl} ABNL
              </span>
            )}
          </div>
          <div className="space-y-2">
            {data.labs!.map((group, i) => (
              <LabGroupTable key={i} name={group.name} labs={group.labs} />
            ))}
          </div>
        </div>
      )}

      {/* Imaging & Diagnostics */}
      {hasImaging && (
        <div className="space-y-2">
          <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold">
            Imaging &amp; Diagnostics
          </span>
          <ImagingFindings findings={data.imaging!} />
        </div>
      )}
    </div>
  )
}
